import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Slider from 'react-slick';
import { FaCampground, FaHotel } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useScrollToTop from '../ScrollToTop';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './place.css';

const Lnaivasha = () => {
  useScrollToTop();
  const { t } = useTranslation('naivasha');
  const options = t('accommodation.options', { returnObjects: true });

  return (
    <motion.div
      className="place-page"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {/* Helmet SEO */}
     <Helmet>
  <title>Lake Naivasha Safaris | Boat Rides, Wildlife & Lodges | Olenini Safaris</title>
  <meta
    name="description"
    content="Discover Lake Naivasha’s serene waters, wildlife, flamingos, and luxury lodges. Plan your escape with Olenini Safaris today."
  />
  <link rel="canonical" href="https://oleninisafaris.com/lake-naivasha" />
  <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />

  {/* Open Graph */}
  <meta property="og:title" content="Lake Naivasha Safari | Olenini Safaris" />
  <meta property="og:description" content="Explore Lake Naivasha’s wildlife, boat safaris, and scenic lodges with Olenini Safaris." />
  <meta property="og:image" content="https://oleninisafaris.com/images/HIPPOS.jpg" />
  <meta property="og:url" content="https://oleninisafaris.com/lake-naivasha" />
  <meta property="og:type" content="place" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Lake Naivasha Safari | Olenini Safaris" />
  <meta name="twitter:description" content="Discover Lake Naivasha’s boat safaris, flamingos, and wildlife retreats with Olenini Safaris." />
  <meta name="twitter:image" content="https://oleninisafaris.com/images/HIPPOS.jpg" />

  {/* JSON-LD Structured Data */}
  <script type="application/ld+json">
    {`
    {
      "@context": "https://schema.org",
      "@type": "TouristAttraction",
      "name": "Lake Naivasha",
      "description": "Lake Naivasha is a freshwater lake in Kenya's Great Rift Valley, famous for its boat rides, hippos, and scenic lodges.",
      "image": "https://oleninisafaris.com/images/HIPPOS.jpg",
      "url": "https://oleninisafaris.com/lake-naivasha",
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -0.7167,
        "longitude": 36.4333
      },
      "containedInPlace": {
        "@type": "TouristDestination",
        "name": "Kenya"
      },
      "provider": {
        "@type": "TravelAgency",
        "name": "Olenini Tours & Safaris",
        "url": "https://oleninisafaris.com"
      },
      "sameAs": [
        "https://en.wikipedia.org/wiki/Lake_Naivasha",
        "https://www.google.com/maps/place/Lake+Naivasha"
      ]
    }
    `}
  </script>
</Helmet>


      {/* Hero Section */}
      <section className="place-hero">
        <img src="/images/HIPPOS.jpg" alt="Hippos relaxing in Lake Naivasha" className="hero-image" />
        <div className="hero-text">
          <h1>{t('heroText.title')}</h1>
          <p>{t('heroText.description')}</p>
        </div>
      </section>

      {/* About Section */}
      <section className="place-about">
        <h2>{t('about.title')}</h2>
        <p dangerouslySetInnerHTML={{ __html: t('about.description') }} />
      </section>

      {/* Highlights */}
      <section className="place-highlights">
        <div className="card">
          <h3>{t('highlights.title1')}</h3>
          {t('highlights.paragraphs1', { returnObjects: true }).map((para, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: para }} />
          ))}
        </div>

        <div className="card">
          <h3>{t('highlights.title2')}</h3>
          <ul>
            {t('highlights.activities', { returnObjects: true }).map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
        </div>
      </section>

      {/* Carousel & Tips */}
      <section className="place-highlights alt-layout">
        <div className="card unified-card">
          <div className="image-carousel">
            <Slider
              autoplay
              autoplaySpeed={3000}
              infinite
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              dots={false}
              pauseOnHover
              swipeToSlide
            >
              <div><img src="/images/birdflying.jpg" alt="Bird soaring over Lake Naivasha" className="slick-image" loading="lazy" /></div>
              <div><img src="/images/LNAKURU.jpg" alt="View of Lake Naivasha shoreline" className="slick-image" loading="lazy" /></div>
              <div><img src="/images/RED.jpg" alt="Sunset skyline at Lake Naivasha" className="slick-image" loading="lazy" /></div>
              <div><img src="/images/gira.jpg" alt="Giraffe in the savannah around Lake Naivasha" className="slick-image" loading="lazy" /></div>
            </Slider>
          </div>

          <div className="text-content">
            <h3>{t('visitTips.title')}</h3>
            <ul>
              {t('visitTips.tips', { returnObjects: true }).map((tip, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: tip }} />
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="place-gallery">
        <h2>Gallery</h2>
        <div className="gallery-grid">
          <img src="/images/_12A0263.jpg" alt="Scenic landscape around Lake Naivasha" loading="lazy" />
          <img src="/images/VASHA.jpg" alt="Wildlife near Lake Naivasha" loading="lazy" />
          <img src="/images/bird (2).jpg" alt="Close-up of birds near Lake Naivasha" loading="lazy" />
        </div>
      </section>

      {/* Accommodation */}
      <section className="place-accommodation">
        <h2>{t('accommodation.title')}</h2>
        <p>{t('accommodation.intro')}</p>

        <div className="accommodation-options">
          <div className="option-card">
            <FaCampground className="icon" />
            <h3>{options[0].title}</h3>
            <p>{options[0].description}</p>
          </div>

          <div className="option-card">
            <FaHotel className="icon" />
            <h3>{options[1].title}</h3>
            <p>{options[1].description}</p>
          </div>
        </div>

        <p className="note">{t('accommodation.note')}</p>
      </section>

      {/* Call to Action */}
      <section className="place-cta" aria-label="Call to Action Section">
        <h2>{t('cta.title')}</h2>
        <p>{t('cta.description')}</p>
        <Link to="/contact?source=naivashadestination" className="book-now">
          {t('cta.buttonText')}
        </Link>
      </section>
    </motion.div>
  );
};

export default Lnaivasha;
